import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { FormCard, MessageTile } from "@suralink/samurai/ui";
import { Button, Typography } from "@material-ui/core";
import { CircleExclamation, CheckCircle } from "@suralink/samurai/components";
import { Link } from "react-router-dom";
import { Error } from "@suralink/samurai/icons";
import { TextInput } from "@suralink/samurai/components";
import { RouteComponentProps } from "react-router-dom";
import { connect } from "react-redux";
import * as actions from "../redux/actions";
import styles from "../styles/SSOLogin.module.scss";
import MessageChatSquare from "../icons/MessageChatSqaure";
import {
  getQueryParamsFromUrl,
  supportChat,
  isCustomSubdomain,
} from "../utils/helpers";
import { useFlag } from "../utils/gitlab-feature-flags";
import { FeatureFlags } from "../enums/featureFlags";
import { useTheme, ThemeProvider } from "@material-ui/core/styles";
import { Snackbar } from "@material-ui/core";
import { suraTheme } from "@suralink/samurai/themes";
import { LoadingSpinner } from "@suralink/samurai/components";
import { isValidEmailFormat } from "@suralink/samurai/helpers/sfs";
import LogoHeader from "../components/SSOLogin/LogoHeader";
import { MfaBypassTokenDuration } from "../enums/mfaBypassTokens";
import Mfa from "../components/SSOLogin/Mfa";
import MfaRecovery from "../components/SSOLogin/MfaRecovery";
import SubdomainSearch from "./SubdomainSearch";

interface ComponentProps {}

interface StateProps {
  passwordSaved: boolean;
  onMfaStep: boolean;
  userErrState: boolean;
  loginErrorState: {
    message: string;
    status: number;
  };
  resendVerificationEmailStatus: number;
  reactivateAccountStatus: number;
}

interface DispatchProps {
  emailLoginAction: (email: string) => any;
  emailPasswordLoginAction: (
    authUrl: string,
    data: {
      email: string;
      password: string;
      mfaCode?: string;
      mfaBypassDuration?: any;
      clientId: string;
      redirectUri: string;
      reCaptchaToken?: string;
    }
  ) => any;
  loginSuccessAction: (res: any) => any;
  loginFailureAction: (res: any) => any;
  toggleUserErrorAction: (userErrState: boolean) => any;
  toggleMfaAction: (show: boolean) => any;
  clearErrorStateAction: () => any;
}

export enum errorResponse {
  notVerified = "User email has not been verified",
  invalidNameOrPassword = "Invalid username or password",
  accountLocked = "Account temporarily locked due to failed mfa login attempts.",
}

export enum TempMessage {
  genericError = "We had trouble signing you in. Please try again. If the issue persists, contact Suralink support.",
  anotherLogin = "We signed you out because you've signed in on another browser or device. If this was not you, alert Suralink support, or your firm admin, immediately.",
  inactive = "To keep your data secure, we temporarily deactivated your sign in credentials due to inactivity. Please check your email for instructions to reactivate.",
  acceptedInvite = "Your invitation has already been accepted. Please sign in.",
  notFoundInvite = "We’re unable to find your invitation. If you think this is a mistake, please contact your firm admin.",
  accountVerified = "Your account has already been verified. Please sign in.",
  reactivatedSuccess = "Your account has been reactivated. Please sign in.",
  reactivateError = "We had trouble reactivating your account, please try again later. If this issue persists, contact Suralink support for assistance.",
  loggedOut = "You have signed out. This may have been due to inactivity.",
}

type Props = StateProps & ComponentProps & RouteComponentProps & DispatchProps;

const useReCAPTCHA = process.env.REACT_APP_USE_RECAPTCHA !== "false";

let mfaTimeout: any;

const SSOLogin: React.FC<Props> = ({
  passwordSaved,
  userErrState,
  onMfaStep,
  emailLoginAction,
  emailPasswordLoginAction,
  loginSuccessAction,
  loginFailureAction,
  toggleUserErrorAction,
  toggleMfaAction,
  clearErrorStateAction,
  loginErrorState,
  reactivateAccountStatus,
  history,
}) => {
  const [showMfaRecoveryContent, setShowMfaRecoveryContent] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [reCaptchaScriptAdded, setReCaptchaScriptAdded] = useState(false);
  const [mfaError, setMfaError] = useState("");
  const [showPasswordResetSuccess, setShowPasswordResetSuccess] =
    useState(false);
  const [showSubdomainSearch, setSubdomainSearch] = useState(false);

  const [displayMessage, setDisplayMessage] = useState("");
  const [alertType, setAlertType] = useState("");

  const theme: any = useTheme();

  const loginForm = useRef<HTMLFormElement>(null);

  const queryParams = getQueryParamsFromUrl(window.location.href);
  const {
    coBrandingEnabled,
    clientId,
    redirectUri,
    sessionInvalid,
    inviteAccepted,
    inviteNotFound,
    emailVerified,
    firmName,
    state,
    logout,
  } = queryParams;

  let firmLogo;
  if (state) {
    const cobrandingData = JSON.parse(state);
    firmLogo = cobrandingData.firmLogo;
  }

  const flagZendesk = useFlag(FeatureFlags.ZendeskChat);

  window.clientId = clientId;
  window.redirectUri = redirectUri;

  const authUrl = process.env.REACT_APP_BACKEND_URL;
  const authRoute = `${authUrl}/oauth/token`;
  const isOnSubdomain = isCustomSubdomain(redirectUri);

  // if critical url params are missing send them to suralink.com
  useEffect(() => {
    if (!clientId || !redirectUri) {
      window.location.href = "https://www.suralink.com";
    }
    return;
  }, [clientId, redirectUri]);

  useEffect(() => {
    if (passwordSaved && !userErrState) {
      setShowPasswordResetSuccess(true);
    }
  }, [passwordSaved, userErrState]);

  useEffect(() => {
    return () => {
      // clear mfa timeout on unmount, in case of user navigation
      if (mfaTimeout) clearTimeout(mfaTimeout);
    };
  }, []);

  useEffect(() => {
    if (logout && displayMessage === "") {
      setUserMessage("notify", TempMessage.loggedOut);
    }
    if (sessionInvalid && displayMessage === "") {
      setUserMessage("warning", TempMessage.anotherLogin);
    }
    if (inviteAccepted && displayMessage === "") {
      setUserMessage("notify", TempMessage.acceptedInvite);
    }
    if (inviteNotFound && displayMessage === "") {
      setUserMessage("error", TempMessage.notFoundInvite);
    }
    if (emailVerified && displayMessage === "") {
      setUserMessage("notify", TempMessage.accountVerified);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionInvalid, inviteAccepted, inviteNotFound, emailVerified, logout]);

  useEffect(() => {
    if (reactivateAccountStatus === 204) {
      setUserMessage("alert", TempMessage.reactivatedSuccess);
    }
    if (reactivateAccountStatus === 500) {
      setUserMessage("error", TempMessage.reactivateError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reactivateAccountStatus]);

  const removeUserMessage = () => {
    setUserMessage("", "");
    toggleUserErrorAction(false);
  };

  const InfoIcon = () => {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M9.99967 0.833328C4.93706 0.833328 0.833008 4.93738 0.833008 10C0.833008 15.0626 4.93706 19.1667 9.99967 19.1667C15.0623 19.1667 19.1663 15.0626 19.1663 10C19.1663 4.93738 15.0623 0.833328 9.99967 0.833328ZM9.99967 5.83333C9.53944 5.83333 9.16634 6.20642 9.16634 6.66666C9.16634 7.1269 9.53944 7.5 9.99967 7.5H10.008C10.4682 7.5 10.8413 7.1269 10.8413 6.66666C10.8413 6.20642 10.4682 5.83333 10.008 5.83333H9.99967ZM10.833 10C10.833 9.53976 10.4599 9.16666 9.99967 9.16666C9.53944 9.16666 9.16634 9.53976 9.16634 10V13.3333C9.16634 13.7936 9.53944 14.1667 9.99967 14.1667C10.4599 14.1667 10.833 13.7936 10.833 13.3333V10Z"
          fill="#9C27B0"
        />
      </svg>
    );
  };

  const setUserMessage = (type: string, message: string) => {
    setDisplayMessage(message);
    setAlertType(type);
    toggleUserErrorAction(true);
    if (message === TempMessage.genericError) {
      setTimeout(() => {
        removeUserMessage();
      }, 8000);
    }
  };

  // removes error message on input text for most messages
  useEffect(() => {
    if (
      email.length > 0 &&
      (displayMessage === TempMessage.anotherLogin ||
        TempMessage.inactive ||
        TempMessage.acceptedInvite ||
        TempMessage.notFoundInvite ||
        TempMessage.accountVerified ||
        TempMessage.reactivatedSuccess ||
        TempMessage.reactivateError)
    ) {
      removeUserMessage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const EmailInputIsValid = typeof email === "string" && email.length > 0;

  const checkForm = () => {
    if (!password) {
      setPasswordError(true);
      setPasswordErrorMessage("Please enter a password.");
    }
    let formValid = isValidEmailFormat(email);
    if (formValid && EmailInputIsValid && password) {
      return true;
    } else {
      setEmailError(true);
      setEmailErrorMessage("Please enter a valid email address.");
      if (!EmailInputIsValid) {
        setEmailError(true);
        setEmailErrorMessage("Email is required.");
      }
      return false;
    }
  };

  const handleSignIn = (e: any) => {
    const validForm = checkForm();
    if (validForm) {
      setLoading(true);
      if (!useReCAPTCHA) {
        signInWithEmailPassword({ e, reCaptchaToken: "" });
      } else {
        window.grecaptcha.ready(function () {
          window.grecaptcha
            .execute(process.env.REACT_APP_RECAPTCHA_KEY, {
              action: "submit",
            })
            .then(function (token: string) {
              signInWithEmailPassword({ e, reCaptchaToken: token });
            });
        });
      }
    }
  };

  async function signInWithEmailPassword({
    e,
    reCaptchaToken,
    mfaCode,
    rememberMfaChecked,
    rememberMfaDuration,
  }: {
    e: any;
    reCaptchaToken: string;
    mfaCode?: string;
    rememberMfaChecked?: boolean;
    rememberMfaDuration?: MfaBypassTokenDuration;
  }): Promise<void> {
    const formValid = checkForm();
    if (!formValid) return;

    let loginData: {
      email: string;
      password: string;
      clientId: string;
      redirectUri: string;
      [key: string]: string | undefined;
    } = {
      email,
      password,
      clientId,
      redirectUri,
    };
    if (mfaCode) {
      loginData.mfaCode = mfaCode;
    }
    if (onMfaStep && rememberMfaChecked) {
      loginData.mfaBypassDuration =
        MfaBypassTokenDuration[rememberMfaDuration as any];
    }
    if (useReCAPTCHA) {
      loginData.reCaptchaToken = reCaptchaToken;
    }
    try {
      await emailPasswordLoginAction(authUrl || "", loginData).then(
        (res: any) => {
          if (loginForm && loginForm.current) {
            loginForm.current.submit();
            loginSuccessAction(res);
          }
        }
      );
    } catch (error: any) {
      setTimeout(() => {
        setLoading(false);
        let message = error.response?.data?.message;
        if (message === errorResponse.invalidNameOrPassword) {
          setPasswordError(true);
          setPasswordErrorMessage(
            "The email and password you entered do not match."
          );
          toggleUserErrorAction(true);
        } else if (message && !!message.match(/(Account expired)/)) {
          setPassword("");
          setPasswordError(false);
          if (onMfaStep) {
            resetFromMfaStep();
          }
          setUserMessage("warning", TempMessage.inactive);
        } else if (error.response?.status === 401) {
          clearErrorStateAction();
          toggleMfaAction(true);
          mfaTimeout = setTimeout(() => {
            setMfaError("");
            resetFromMfaStep();
            loginFailureAction({
              data: {
                message: "Code entry time expired, please log in again.",
              },
              status: 1,
            });
            setPasswordError(false);
            if (!showMfaRecoveryContent) {
              setUserMessage(
                "warning",
                "Your two-factor authentication time expired. Please sign in to try again. "
              );
            }
          }, 120000);
        } else if (message === errorResponse.notVerified) {
          history.push(
            `/create-account/verify${history.location.search}&email=${email}`
          );
        } else if (
          error.response?.status === 423 &&
          message === errorResponse.accountLocked
        ) {
          toggleMfaAction(false);
          setPassword("");
          setUserMessage(
            "error",
            "Due to too many failed sign in attempts, we have temporarily locked your account. If you think this is a mistake, please contact Suralink support."
          );
        } else if (
          !!error.response?.data?.message.match(/(MFA)/) &&
          error.response?.status === 403
        ) {
          const attemptsMatch = message.match(/\d/);
          let remainingAttempts = attemptsMatch
            ? parseInt(attemptsMatch[0])
            : NaN;
          let counterText = "";
          if (!isNaN(remainingAttempts)) {
            counterText = `The passcode you provided is incorrect. You have ${remainingAttempts} more attempt${
              remainingAttempts > 1 ? "s" : ""
            }.`;
          }
          setMfaError(counterText);
        } else {
          setUserMessage("error", TempMessage.genericError);
        }
      }, 1000);
    }
  }

  const resetFromMfaStep = (inputOnly?: boolean) => {
    if (!inputOnly) {
      setPassword("");
      toggleMfaAction(false);
    }
  };

  // handle when user clicks 'get a recovery token' on mfa step
  function handleClickGetMfaRecoveryToken() {
    setPasswordError(false);
    setPasswordErrorMessage("");
    setEmailError(false);
    setEmailErrorMessage("");
    setMfaError("");
    resetFromMfaStep();
    setShowMfaRecoveryContent(true);
    clearTimeout(mfaTimeout);
  }

  const handleOpenChat = () => {
    supportChat(flagZendesk);
  };

  useEffect(() => {
    if (!reCaptchaScriptAdded && useReCAPTCHA && !window.grecaptcha) {
      var script = document.createElement("script");
      script.type = "text/javascript";
      script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_KEY}`;
      document.body.appendChild(script);
      setReCaptchaScriptAdded(true);
    }
  }, [reCaptchaScriptAdded]);

  return (
    <ThemeProvider theme={suraTheme}>
      <div
        style={{
          backgroundColor: theme.palette.primary[50],
          width: "auto",
        }}
        className={styles.main}
      >
        <div
          className={classNames(styles.login, {
            [styles.onMfaStepDisplay]: onMfaStep,
          })}
          style={{
            borderRadius: `${showSubdomainSearch ? "0.5rem" : "0"}`,
          }}
        >
          {showSubdomainSearch ? (
            <SubdomainSearch toggleSubdomain={setSubdomainSearch} />
          ) : (
            <>
              {!onMfaStep && !showMfaRecoveryContent && (
                <div className={styles.marketing}></div>
              )}
              <div
                className={styles.inputModal}
                style={{
                  width: `${showMfaRecoveryContent ? "40rem" : "29.125rem"}`,
                }}
              >
                <form
                  action={authRoute}
                  method="POST"
                  ref={loginForm}
                  noValidate
                >
                  <FormCard
                    style={{
                      minHeight: "650px",
                      margin: 0,
                      padding: "4rem 3.313rem",
                      border: "none",
                    }}
                  >
                    {false && (
                      <MessageTile
                        state="error"
                        message="Suralink is experiencing issues."
                        subtext="We are investigating an issue that is preventing users from logging into our system."
                        subsubtext="Please check https://status.suralink.com for updates."
                        style={{
                          position: "relative",
                          top: `${coBrandingEnabled ? "-13px" : "-30px"}`,
                        }}
                        icon={<Error />}
                        dismissAlertCb={() => null}
                      />
                    )}
                    {userErrState && displayMessage ? (
                      <div
                        className={classNames(styles.messageTileMain, {
                          [styles.alert]: alertType === "alert",
                          [styles.warning]: alertType === "warning",
                          [styles.error]: alertType === "error",
                          [styles.notify]: alertType === "notify",
                        })}
                      >
                        <div className={styles.top}>
                          <span
                            className={styles.messageIcon}
                            style={{ alignItems: "flex-start" }}
                          >
                            {alertType === "warning" ? (
                              <CircleExclamation
                                size={20}
                                fillColor={theme.palette.warning[500]}
                              />
                            ) : alertType === "alert" ? (
                              <CheckCircle
                                height={20}
                                width={20}
                                fillColor="#377D61"
                              />
                            ) : alertType === "error" ? (
                              <CircleExclamation
                                size={20}
                                fillColor={theme.palette.critical[500]}
                              />
                            ) : alertType === "notify" ? (
                              <InfoIcon />
                            ) : null}
                          </span>
                          <Typography
                            variant="body2"
                            style={{
                              alignItems: "flex-start",
                            }}
                          >
                            {displayMessage}
                          </Typography>
                        </div>
                      </div>
                    ) : null}
                    <div className={styles.loginContainer}>
                      <LogoHeader
                        showCompanyLogo={isOnSubdomain}
                        firmLogo={firmLogo}
                        firmName={firmName}
                      />
                      <hr
                        className={classNames(styles.topDividerEmailText, {
                          [styles.hrMfaRecoveryContentVisible]:
                            showMfaRecoveryContent,
                        })}
                      />
                      {showMfaRecoveryContent && (
                        <MfaRecovery
                          goToSignIn={() => setShowMfaRecoveryContent(false)}
                          coBrandingEnabled={coBrandingEnabled}
                        />
                      )}
                      {onMfaStep && !showMfaRecoveryContent && (
                        <Typography
                          variant="body1"
                          className={styles.mfaHeader}
                        >
                          To sign in, enter the two-factor passcode provided by
                          your authenticator app.
                        </Typography>
                      )}
                      {onMfaStep && !showMfaRecoveryContent && (
                        <>
                          <input type="hidden" name="email" value={email} />
                          <input
                            type="hidden"
                            name="password"
                            value={password}
                          />
                          <Mfa
                            setPasswordMissing={setPasswordError}
                            passwordMissing={passwordError}
                            checkForm={checkForm}
                            useReCAPTCHA={useReCAPTCHA}
                            handleSignIn={signInWithEmailPassword}
                            handleClickGetMfaRecoveryToken={
                              handleClickGetMfaRecoveryToken
                            }
                            setUserMessage={setUserMessage}
                            coBrandingEnabled={coBrandingEnabled}
                            mfaError={mfaError}
                            setMfaError={setMfaError}
                          />
                        </>
                      )}
                      <React.Fragment>
                        <input
                          type="hidden"
                          name="clientId"
                          value={clientId!}
                        />
                        <input
                          type="hidden"
                          name="redirectUri"
                          value={redirectUri!}
                        />
                        <input
                          type="hidden"
                          name="grantType"
                          value="password"
                        />
                        <div style={{ paddingTop: "0.5rem" }}>
                          {!onMfaStep && !showMfaRecoveryContent && (
                            <div>
                              <TextInput
                                value={email}
                                label="Email address"
                                onChange={(e: any) => {
                                  setEmail(e.target.value.trim());
                                  setEmailError(false);
                                  setEmailErrorMessage("");
                                }}
                                id="email"
                                fullWidth
                                error={emailError}
                                errorMessage={emailErrorMessage}
                              />
                              <div className={styles.passwordArea}>
                                <input
                                  type="hidden"
                                  name="email"
                                  value={email}
                                />
                                <input
                                  type="hidden"
                                  value={password}
                                  name="password"
                                />
                                <TextInput
                                  value={password}
                                  label="Password"
                                  onChange={(e: any) => {
                                    setPassword(e.target.value.trim());
                                    setPasswordError(false);
                                  }}
                                  id="password"
                                  fullWidth
                                  error={passwordError}
                                  errorMessage={passwordErrorMessage}
                                  muiChildrenProps={{
                                    InputProps: {
                                      type: "password",
                                    },
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                        {!onMfaStep && !showMfaRecoveryContent && (
                          <>
                            <Button
                              color="primary"
                              variant="contained"
                              style={{
                                width: "100%",
                                margin: "1rem auto 0 auto",
                              }}
                              onClick={(e: any) => {
                                e.preventDefault();
                                if (checkForm()) {
                                  handleSignIn(e);
                                }
                              }}
                              type="submit"
                            >
                              {loading ? (
                                <LoadingSpinner color="white" size={16} />
                              ) : (
                                "Sign In"
                              )}
                            </Button>
                            <div
                              className={styles.forgotWrapper}
                              onClick={() => {
                                clearErrorStateAction();
                              }}
                            >
                              <Link
                                className={styles.forgotButton}
                                to={{
                                  pathname: "/login/reset",
                                  state: {
                                    clientId,
                                    redirectUri,
                                    state,
                                    coBrandingEnabled,
                                    search: history.location.search,
                                  },
                                }}
                              >
                                Forgot your password?
                              </Link>
                            </div>
                            <hr
                              style={{
                                margin: "2.5rem auto",
                                marginBottom: "2rem",
                              }}
                            />
                            <div style={{ textAlign: "center" }}>
                              <Button
                                variant="text"
                                onClick={() => setSubdomainSearch(true)}
                              >
                                {isOnSubdomain
                                  ? "Sign in with a different firm"
                                  : "Sign in with SSO"}
                              </Button>
                            </div>
                            <div
                              style={{ textAlign: "center", marginTop: "2rem" }}
                            >
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={handleOpenChat}
                              >
                                <span
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                  }}
                                >
                                  <MessageChatSquare />
                                  Live chat support
                                </span>
                              </Button>
                            </div>
                          </>
                        )}
                      </React.Fragment>
                    </div>
                  </FormCard>
                </form>
              </div>
            </>
          )}
        </div>
        <Snackbar
          open={showPasswordResetSuccess}
          autoHideDuration={8000}
          onClose={() => {
            setShowPasswordResetSuccess(false);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          message={"Your password has been updated."}
          style={{ marginBottom: "1.5rem" }}
          ContentProps={{
            style: {
              padding: "0.5rem 2rem",
              textAlign: "center",
              backgroundColor: theme.palette.grey[900],
              borderRadius: "8px",
              justifyContent: "center",
            },
          }}
        />
      </div>
    </ThemeProvider>
  );
};

const mstp = (state: InitialStateModel): StateProps => {
  const {
    passwordSaved,
    onMfaStep,
    userErrState,
    loginErrorState,
    resendVerificationEmailStatus,
    reactivateAccountStatus,
  } = state;
  return {
    passwordSaved,
    onMfaStep,
    userErrState,
    loginErrorState,
    resendVerificationEmailStatus,
    reactivateAccountStatus,
  };
};

const matp = ((): DispatchProps => {
  const {
    emailLoginAction,
    emailPasswordLoginAction,
    loginSuccessAction,
    loginFailureAction,
    toggleUserErrorAction,
    toggleMfaAction,
    clearErrorStateAction,
  } = actions;
  return {
    emailLoginAction,
    emailPasswordLoginAction,
    loginSuccessAction,
    loginFailureAction,
    toggleUserErrorAction,
    toggleMfaAction,
    clearErrorStateAction,
  };
})();

export default connect(mstp, matp)(SSOLogin);
